import { Component, OnInit, AfterViewInit, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AppName } from 'applib';
import { MssLibModule } from 'msslib';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [RouterOutlet, MssLibModule],
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly themeStyleId = 'clubhub-theme-overrides';

  ngOnInit() {
    (window as any)['appName'] = AppName.ClubHub;
    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
  }

  get isSmartrfit() {
    return this.router.url.startsWith('/smartrfit');
  }

  get isSmartrCriteriaOutcomePage() {
    return /^\/smartrcriteria\/[0-9]{1}\/outcome/.test(this.router.url);
  }

  addCSSRule(sheet: any, selector: string, rules: string) {
    const ruleCount = sheet?.cssRules?.length ?? sheet?.rules?.length ?? 0;
    if ('insertRule' in sheet) {
      sheet.insertRule(`${selector}{${rules}}`, ruleCount);
    } else if ('addRule' in sheet) {
      sheet.addRule(selector, rules, ruleCount);
    }
  }

  private getThemeStyleSheet(): CSSStyleSheet | null {
    const existing = this.document.getElementById(this.themeStyleId) as HTMLStyleElement | null;
    if (existing?.sheet) {
      return existing.sheet as CSSStyleSheet;
    }

    const styleEl = this.document.createElement('style');
    styleEl.id = this.themeStyleId;
    this.document.head.appendChild(styleEl);
    return (styleEl.sheet as CSSStyleSheet) ?? null;
  }

  private clearStyleSheet(sheet: CSSStyleSheet) {
    const count = sheet?.cssRules?.length ?? 0;
    for (let i = count - 1; i >= 0; i--) {
      sheet.deleteRule(i);
    }
  }

  hexToRGB(hex: string) {
    let r = '0',
      g = '0',
      b = '0';
    if (hex.length === 4) {
      r = `0x${hex[1]}${hex[1]}`;
      g = `0x${hex[2]}${hex[2]}`;
      b = `0x${hex[3]}${hex[3]}`;
    } else if (hex.length === 7) {
      r = `0x${hex[1]}${hex[2]}`;
      g = `0x${hex[3]}${hex[4]}`;
      b = `0x${hex[5]}${hex[6]}`;
    }
    return `${+r} ${+g} ${+b}`;
  }

  ngAfterViewInit() {
    this.activatedRoute.queryParams.subscribe((params) => {
      const rawColor = params['color'];
      if (!rawColor) {
        return;
      }

      const normalized = String(rawColor).replace(/^#/, '').trim();
      if (!/^(?:[0-9a-fA-F]{3}){1,2}$/.test(normalized)) {
        return;
      }
      const color = `#${normalized}`;

      const sheet = this.getThemeStyleSheet();
      if (!sheet) {
        return;
      }
      this.clearStyleSheet(sheet);

      // Theme Color Overwrites
      this.addCSSRule(sheet, '#step-nav.active, #step-nav.active:not(:last-of-type) ::after', `background-color: ${color} !important;`);
      this.addCSSRule(
        sheet,
        '.form-control:not(:focus) .input-group-text, .custom-radio-label .input-group-text, .input-group-text, .input-group-append .input-group-text',
        `background-color: ${color} !important; border-color: ${color} !important; color: #fff !important;`,
      );
      this.addCSSRule(
        sheet,
        '.btn-primary:not(:disabled):not(.disabled):active, .btn-primary:not(:disabled):not(.disabled).active, .show > .btn-primary.dropdown-toggle',
        `background-color: ${color} !important; border-color: ${color} !important; color: #fff !important;`,
      );
      this.addCSSRule(sheet, 'button.btn-primary', `background-color: ${color} !important; border-color: ${color} !important;`);
      this.addCSSRule(sheet, '.btn:focus, .btn.focus', `box-shadow: 0 0 0 0.2rem rgb(${this.hexToRGB(color)} / 70%) !important;`);
      this.addCSSRule(
        sheet,
        '.form-control:focus',
        `border-color: ${color} !important; box-shadow: 0 0 0 0.2rem rgb(${this.hexToRGB(color)} / 70%) !important;`,
      );
      this.addCSSRule(sheet, '.wrapper:focus-within', `box-shadow: 0 0 0 0.2rem rgb(${this.hexToRGB(color)} / 70%) !important;`);
      this.addCSSRule(sheet, '.btn-primary:hover', `border-color: ${color} !important;`);
      this.addCSSRule(
        sheet,
        '.btn-primary:not(:disabled):not(.disabled).active:focus, .btn-primary:not(:disabled):not(.disabled):active:focus, .show>.btn-primary.dropdown-toggle:focus',
        `box-shadow: 0 0 0 0.2rem rgb(${this.hexToRGB(color)} / 70%) !important;`,
      );
      this.addCSSRule(sheet, 'a', `color: ${color}`);
      this.addCSSRule(sheet, 'a:hover', `color: ${color}`);
      this.addCSSRule(sheet, '.separator', `background-color: ${color} !important;`);
      this.addCSSRule(sheet, '.results-circle::after', `border: 4px solid ${color} !important;`);
      this.addCSSRule(sheet, '.card .card-header', `background-color: ${color} !important;`);
      this.addCSSRule(sheet, '.bg-primary', `background-color: ${color} !important;`);
      this.addCSSRule(sheet, '.progress-bar ', `background-color: ${color} !important;`);
      this.addCSSRule(
        sheet,
        '.form-control.is-valid',
        `background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='rgb(${this.hexToRGB(
          color,
        )}' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e") !important;`,
      );
      this.addCSSRule(sheet, '.custom-radio-wrapper label.active.disabled', `background-color: ${color} !important;`);
    });
  }
}
